import { MainLayout } from '@templates/index';
import { LEGAL_CONFIG } from '@/config/legal';
import { Link } from 'react-router-dom';

interface CookieRow {
  name: string;
  purpose: string;
  duration: string;
  type: string;
}

const ESSENTIAL_COOKIES: CookieRow[] = [
  {
    name: 'access_token',
    purpose: 'Kullanıcı oturumunun doğrulanması ve korunması',
    duration: 'Oturum süresi',
    type: 'Zorunlu',
  },
  {
    name: 'refresh_token',
    purpose: 'Oturumun güvenli şekilde yenilenmesi',
    duration: '30 gün',
    type: 'Zorunlu',
  },
  {
    name: 'cart_session',
    purpose: 'Sepet içeriğinin geçici olarak saklanması',
    duration: 'Oturum süresi',
    type: 'Zorunlu',
  },
  {
    name: 'csrf_token',
    purpose: 'Çapraz site istek sahteciliğine (CSRF) karşı koruma',
    duration: 'Oturum süresi',
    type: 'Zorunlu',
  },
];

const ANALYTICS_COOKIES: CookieRow[] = [
  {
    name: '_ga, _gid',
    purpose: 'Google Analytics — anonim ziyaretçi istatistikleri',
    duration: '2 yıl / 24 saat',
    type: 'Analitik',
  },
  {
    name: '_fbp',
    purpose: 'Meta (Facebook) Pixel — reklam performansı ölçümü',
    duration: '90 gün',
    type: 'Pazarlama',
  },
];

function CookieTable({ rows }: { rows: CookieRow[] }) {
  return (
    <div className="overflow-x-auto mb-6">
      <table className="min-w-full border border-gray-200 rounded-lg text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left font-semibold text-gray-900 border-b">Çerez Adı</th>
            <th className="px-4 py-3 text-left font-semibold text-gray-900 border-b">Amaç</th>
            <th className="px-4 py-3 text-left font-semibold text-gray-900 border-b">Süre</th>
            <th className="px-4 py-3 text-left font-semibold text-gray-900 border-b">Tür</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {rows.map((row) => (
            <tr key={row.name}>
              <td className="px-4 py-3 text-gray-700 font-mono text-xs">{row.name}</td>
              <td className="px-4 py-3 text-gray-700">{row.purpose}</td>
              <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{row.duration}</td>
              <td className="px-4 py-3">
                <span
                  className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                    row.type === 'Zorunlu'
                      ? 'bg-blue-50 text-blue-700'
                      : row.type === 'Analitik'
                        ? 'bg-amber-50 text-amber-700'
                        : 'bg-purple-50 text-purple-700'
                  }`}
                >
                  {row.type}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const CookiesPage = () => {
  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto py-12 px-4">
        <div className="prose prose-lg max-w-none">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Çerez Politikası</h1>
          <p className="text-gray-500 text-sm mb-8">
            Son Güncelleme Tarihi: {LEGAL_CONFIG.lastUpdated}
          </p>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Çerez Nedir?</h2>
            <p className="text-gray-700 leading-relaxed">
              Çerezler (cookies), bir web sitesini ziyaret ettiğinizde tarayıcınız tarafından
              cihazınıza kaydedilen küçük metin dosyalarıdır. Çerezler; oturum yönetimi, tercih
              hatırlama, güvenlik ve analitik gibi amaçlarla kullanılır. Lezzet Yolda olarak
              platformumuzun düzgün çalışması ve size daha iyi bir deneyim sunmak için çerezlerden
              yararlanmaktayız.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              2. Kullandığımız Çerez Türleri
            </h2>

            <h3 className="text-base font-semibold text-gray-800 mb-3">
              2.1 Zorunlu Çerezler
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Platformun temel işlevlerinin çalışması için gereklidir. Bu çerezler olmadan oturum
              açma, sepet yönetimi ve güvenli ödeme gibi hizmetler sunulamaz. Tarayıcı
              ayarlarından devre dışı bırakılabilir; ancak bu durumda platformun bazı özellikleri
              çalışmayabilir.
            </p>
            <CookieTable rows={ESSENTIAL_COOKIES} />

            <h3 className="text-base font-semibold text-gray-800 mb-3">
              2.2 Analitik ve Pazarlama Çerezleri
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Platformun nasıl kullanıldığını anlamamıza ve hizmetlerimizi iyileştirmemize yardımcı
              olur. Bu çerezler yalnızca açık rızanız alındıktan sonra yerleştirilir.
            </p>
            <CookieTable rows={ANALYTICS_COOKIES} />
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              3. Çerez Tercihlerinizi Yönetme
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Çerez tercihlerinizi aşağıdaki yöntemlerle yönetebilirsiniz:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>
                <strong>Tarayıcı Ayarları:</strong> Tüm modern tarayıcılar çerezleri yönetme
                seçeneği sunar. Tarayıcınızın &quot;Ayarlar → Gizlilik&quot; bölümünden çerezleri
                görüntüleyebilir, silebilir veya engelleyebilirsiniz.
              </li>
              <li>
                <strong>Çerez Tercih Merkezi:</strong> Platforma ilk girişinizde sunulan çerez
                onay penceresi üzerinden tercihlerinizi belirleyebilirsiniz.
              </li>
              <li>
                <strong>Google Analytics Devre Dışı Bırakma:</strong>{' '}
                <a
                  href="https://tools.google.com/dlpage/gaoptout"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Google Analytics Opt-out eklentisini
                </a>{' '}
                yükleyerek analitik takibini devre dışı bırakabilirsiniz.
              </li>
            </ul>
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-sm text-amber-800">
                <strong>Önemli:</strong> Zorunlu çerezleri devre dışı bırakmanız durumunda oturum
                açma, ödeme ve sepet gibi temel platform işlevleri çalışmayabilir.
              </p>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              4. Üçüncü Taraf Çerezleri
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Platformumuz, Google Analytics ve Meta Pixel gibi üçüncü taraf hizmetleri
              kullanmaktadır. Bu hizmetlerin çerez politikaları kendi gizlilik belgelerinde
              açıklanmıştır. Lezzet Yolda, üçüncü taraf çerezlerinin içeriğinden sorumlu
              değildir.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              5. KVKK Kapsamında Çerezler
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Analitik ve pazarlama çerezleri, 6698 sayılı KVKK kapsamında kişisel veri
              niteliği taşıyabilir. Bu çerezler yalnızca açık rızanız alınarak kullanılmaktadır.
              Rızanızı dilediğiniz zaman geri alabilirsiniz. Detaylı bilgi için{' '}
              <Link to="/privacy" className="text-primary hover:underline font-medium">
                Gizlilik Politikamızı
              </Link>{' '}
              inceleyebilirsiniz.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Politika Güncellemeleri</h2>
            <p className="text-gray-700 leading-relaxed">
              Bu Çerez Politikası, teknoloji veya mevzuat değişikliklerine bağlı olarak
              güncellenebilir. Önemli değişiklikler platform üzerinden duyurulacaktır.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. İletişim</h2>
            <p className="text-gray-700 leading-relaxed">
              Çerez politikamıza ilişkin sorularınız için{' '}
              <a href={`mailto:${LEGAL_CONFIG.email}`} className="text-primary hover:underline font-medium">
                {LEGAL_CONFIG.email}
              </a>{' '}
              adresine ulaşabilirsiniz.
            </p>
          </section>

          <div className="mt-12 pt-8 border-t border-gray-200 flex flex-wrap gap-4">
            <Link to="/privacy" className="text-primary hover:underline font-medium">
              Gizlilik Politikası
            </Link>
            <Link to="/terms" className="text-primary hover:underline font-medium">
              Kullanım Koşulları
            </Link>
            <Link to="/" className="text-primary hover:underline font-medium">
              ← Ana Sayfaya Dön
            </Link>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default CookiesPage;
